import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { WorkflowTask, FileMetadata } from '../../types';
import LibraryBrowser from '../LibraryBrowser/LibraryBrowser';

interface TaskStartDialogProps {
  open: boolean;
  task: WorkflowTask;
  onClose: () => void;
  onStart: (
    taskId: number,
    fileData: { siteId: string; driveId: string; itemId: string },
    emails: { revisionEmail?: string; approvalEmail?: string; reviewEmail?: string }
  ) => void;
}

interface FileSelectionData {
  siteId: string;
  driveId: string;
  itemId: string;
  name: string;
}

export const TaskStartDialog = ({ open, task, onClose, onStart }: TaskStartDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<FileSelectionData | null>(null);
  const [revisionEmail, setRevisionEmail] = useState('');
  const [approvalEmail, setApprovalEmail] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // TODO: Get these from project configuration/API endpoint
  // For now, hard-coded for testing - replace with actual values
  const siteId = '7de7c338-76a1-472e-8f6a-f2b95c3bb7d5'; // relevantcrm.sharepoint.com siteId
  const libraryName = 'Test Doc Lib One';
  const folderPath = 'Level One A';

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleStart = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a SharePoint file';
    }

    if (task.requiresRevision && (!revisionEmail || !validateEmail(revisionEmail))) {
      newErrors.revisionEmail = 'Valid revision email is required';
    }

    if (task.requiresApproval && (!approvalEmail || !validateEmail(approvalEmail))) {
      newErrors.approvalEmail = 'Valid approval email is required';
    }

    if (task.requiresReview && (!reviewEmail || !validateEmail(reviewEmail))) {
      newErrors.reviewEmail = 'Valid review email is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!selectedFile) return;

    onStart(task.taskId, {
      siteId: selectedFile.siteId,
      driveId: selectedFile.driveId,
      itemId: selectedFile.itemId,
    }, {
      revisionEmail: task.requiresRevision ? revisionEmail : undefined,
      approvalEmail: task.requiresApproval ? approvalEmail : undefined,
      reviewEmail: task.requiresReview ? reviewEmail : undefined
    });
  };

  const canStart = selectedFile && 
    (!task.requiresRevision || validateEmail(revisionEmail)) &&
    (!task.requiresApproval || validateEmail(approvalEmail)) &&
    (!task.requiresReview || validateEmail(reviewEmail));

  const handleFileSelect = (file: FileMetadata) => {
    setSelectedFile({
      siteId: file.siteId,
      driveId: file.driveId,
      itemId: file.itemId,
      name: file.name
    });
    setFileBrowserOpen(false);
    setErrors({ ...errors, file: '' });
  };

  const handleClose = () => {
    setSelectedFile(null);
    setRevisionEmail('');
    setApprovalEmail('');
    setReviewEmail('');
    setErrors({});
    setFileBrowserOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open && !fileBrowserOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Start Task: {task.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <Button 
                variant="outlined" 
                onClick={() => setFileBrowserOpen(true)}
                fullWidth
                sx={{ mb: 1 }}
              >
                {selectedFile ? `Selected: ${selectedFile.name}` : 'Select SharePoint File'}
              </Button>
              {errors.file && (
                <Alert severity="error" sx={{ mt: 1 }}>{errors.file}</Alert>
              )}
            </Box>

            {task.requiresRevision && (
              <TextField
                label="Revision Email"
                type="email"
                value={revisionEmail}
                onChange={(e) => {
                  setRevisionEmail(e.target.value);
                  setErrors({ ...errors, revisionEmail: '' });
                }}
                error={!!errors.revisionEmail}
                helperText={errors.revisionEmail}
                required
                fullWidth
              />
            )}

            {task.requiresApproval && (
              <TextField
                label="Approval Email"
                type="email"
                value={approvalEmail}
                onChange={(e) => {
                  setApprovalEmail(e.target.value);
                  setErrors({ ...errors, approvalEmail: '' });
                }}
                error={!!errors.approvalEmail}
                helperText={errors.approvalEmail}
                required
                fullWidth
              />
            )}

            {task.requiresReview && (
              <TextField
                label="Review Email"
                type="email"
                value={reviewEmail}
                onChange={(e) => {
                  setReviewEmail(e.target.value);
                  setErrors({ ...errors, reviewEmail: '' });
                }}
                error={!!errors.reviewEmail}
                helperText={errors.reviewEmail}
                required
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleStart} disabled={!canStart}>
            Start Task
          </Button>
        </DialogActions>
      </Dialog>

      {fileBrowserOpen && (
        <Dialog open={fileBrowserOpen} onClose={() => setFileBrowserOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Select SharePoint File</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <LibraryBrowser
                onFileSelect={handleFileSelect}
                siteId={siteId}
                libraryName={libraryName}
                folderPath={folderPath}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFileBrowserOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

